// ChatStream.tsx — M2.1 chat-style council message stream.
//
// User feedback (2026-05-10): "前端议会流程修改为 · 聊天对话线性形式 ·
// 而不是按位置逐个发送"
//
// Renders SessionCurrent.messages chronologically (top → bottom · auto-scroll
// to latest) · replaces per-position PersonaCard grid for the M2 council
// experience. Each message:
//
// - speech: persona avatar + name + role + body (markdown) · phase context chip
// - phase_marker: full-width divider with phase name + speakers list
// - iron_kill: vermilion-tinged inline alert with [KILL candidate_x] reason
// - phase_summary: muted rolling summary card (intentional weight: less)
//
// Auto-scroll behavior:
// - User at bottom: stick to bottom on new message (smooth)
// - User scrolled up: do NOT auto-scroll (don't yank · let them read)
// - Threshold: bottom == 64px from bottom edge

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage, PersonaId } from '@/store/session';
import { getPersonaPalette } from '@/ui/theme/persona_palette';

interface ChatStreamProps {
  messages: readonly ChatMessage[];
  /** When true · auto-scroll on each new message (used during live · false in replay) */
  liveMode?: boolean;
  /** Show phase marker dividers */
  showPhaseMarkers?: boolean;
  className?: string;
}

const STICK_THRESHOLD_PX = 64;

export function ChatStream({
  messages,
  liveMode = true,
  showPhaseMarkers = true,
  className = '',
}: ChatStreamProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const stuckBottomRef = useRef(true);

  // Track user scroll position · disable auto-scroll if scrolled up.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      stuckBottomRef.current = distFromBottom < STICK_THRESHOLD_PX;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll on new messages when stuck to bottom.
  useEffect(() => {
    if (!liveMode) return;
    const el = scrollRef.current;
    if (!el) return;
    if (stuckBottomRef.current) {
      // rAF to wait for layout · smooth on subsequent · jump on first
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [messages, liveMode]);

  return (
    <div
      ref={scrollRef}
      className={`overflow-y-auto h-full max-h-[calc(100vh-280px)] pr-2 ${className}`.trim()}
      data-testid="chat-stream"
      role="log"
      aria-live="polite"
      aria-relevant="additions text"
    >
      <div className="space-y-4 pb-8">
        {messages.length === 0 && (
          <div className="text-center py-12 text-ink-2 font-mono text-sm">
            {t('live.chatStream.empty', { defaultValue: '议会准备中…' })}
          </div>
        )}
        {messages.map((m) => {
          if (m.kind === 'phase_marker' && showPhaseMarkers) {
            return <PhaseMarker key={m.id} message={m} />;
          }
          if (m.kind === 'iron_kill') {
            return <IronKillNotice key={m.id} message={m} />;
          }
          if (m.kind === 'speech') {
            return <SpeechBubble key={m.id} message={m} />;
          }
          if (m.kind === 'phase_summary') {
            return <PhaseSummary key={m.id} message={m} />;
          }
          return null;
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SpeechBubble · single persona message bubble
// ─────────────────────────────────────────────────────────────────────────────

function SpeechBubble({ message }: { message: ChatMessage }) {
  const { t } = useTranslation();
  const pid = (message.author ?? 'sage') as PersonaId | string;
  const palette = getPersonaPalette(pid);
  const accentBg = `bg-${palette.accent}-100`;
  const accentText = `text-${palette.accent}-700`;
  const accentBorder = `border-${palette.accent}-400`;
  const name = t(`persona.${pid}.name`, { defaultValue: pid });
  const role = t(`persona.${pid}.role`, { defaultValue: '' });
  const initial = name.slice(0, 1).toUpperCase();

  return (
    <article
      data-testid={`chat-bubble-${message.id}`}
      data-author={pid}
      data-phase={message.phase ?? ''}
      data-complete={message.complete ? '1' : '0'}
      className="flex gap-3 items-start"
      aria-busy={!message.complete}
    >
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-9 h-9 rounded-full ${accentBg} ${accentText} flex items-center justify-center font-serif font-medium text-sm border ${accentBorder}`}
        aria-hidden="true"
      >
        {initial}
      </div>
      {/* Body */}
      <div className="flex-1 min-w-0">
        <header className="flex items-baseline gap-2 mb-1">
          <span className={`font-serif text-base ${accentText}`}>{name}</span>
          {role && (
            <span className="text-xs font-mono text-ink-3">{role}</span>
          )}
          {message.phase && (
            <span className="text-[10px] font-mono text-ink-3 ml-auto opacity-60">
              {message.phase}
            </span>
          )}
        </header>
        <div className="text-sm text-ink leading-relaxed prose-persona max-w-none">
          {message.body ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.body}</ReactMarkdown>
          ) : (
            <span className="text-ink-2 italic">…</span>
          )}
          {!message.complete && (
            <span
              className="inline-block w-2 h-4 bg-ink/40 ml-1 align-middle animate-pulse"
              aria-hidden="true"
            />
          )}
        </div>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PhaseMarker · full-width divider showing phase transition
// ─────────────────────────────────────────────────────────────────────────────

function PhaseMarker({ message }: { message: ChatMessage }) {
  return (
    <div
      data-testid={`chat-phase-marker-${message.id}`}
      className="flex items-center gap-3 my-6"
      role="separator"
    >
      <hr className="flex-1 border-hairline" />
      <span className="font-mono text-xs tracking-hud text-ink-3 uppercase">
        {message.body}
      </span>
      <hr className="flex-1 border-hairline" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IronKillNotice · inline alert when iron kills a candidate
// ─────────────────────────────────────────────────────────────────────────────

function IronKillNotice({ message }: { message: ChatMessage }) {
  return (
    <div
      data-testid={`chat-iron-kill-${message.id}`}
      className="bg-vermilion-50 border-l-4 border-vermilion-400 px-3 py-2 rounded-r-md font-mono text-xs text-vermilion-900"
      role="alert"
    >
      <span className="font-bold mr-2">⚔ Iron</span>
      <span>{message.body}</span>
    </div>
  );
}

function PhaseSummary({ message }: { message: ChatMessage }) {
  const { t } = useTranslation();
  return (
    <aside
      data-testid={`chat-phase-summary-${message.id}`}
      className="ml-12 border border-hairline bg-paper-2 px-3 py-2 text-xs text-ink-2"
      aria-label={t('live.chatStream.phaseSummary', { defaultValue: 'Phase summary' })}
    >
      <div className="mb-1 font-mono uppercase tracking-hud text-ink-3">
        {message.phase ? `${message.phase} · ` : ''}
        {t('live.chatStream.phaseSummary', { defaultValue: 'Phase summary' })}
      </div>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.body}</ReactMarkdown>
    </aside>
  );
}

export default ChatStream;
